import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Alert,
  Spinner,
  Badge,
  ButtonGroup,
} from '../shim/reactstrap';
import { useAuth } from '../contexts/AuthContext';

export const TradingStrategies = () => {
  const { apiClient, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [strategies, setStrategies] = useState(null);
  const [loadError, setLoadError] = useState('');
  // "Only mine" / "All" toggle. Defaults to "Only mine" for everyone
  // (admins and non-admins alike) — admins can flip it to "All" to
  // inspect strategies owned by other users. For non-admins the
  // toggle is still rendered so the UI is consistent, but the server
  // enforces "own only" regardless of the toggle value (so a regular
  // user can't trick the endpoint into returning other users' rows).
  const [scope, setScope] = useState('mine');

  const loadStrategies = useCallback(async () => {
    setLoadError('');
    try {
      const data = await apiClient.getTradingStrategies({
        mineOnly: scope === 'mine',
      });
      setStrategies(data || []);
    } catch (err) {
      setLoadError(err?.message || 'Failed to load trading strategies.');
    }
  }, [apiClient, scope]);

  useEffect(() => { loadStrategies(); }, [loadStrategies]);

  const handleDelete = async (strategy) => {
    if (!window.confirm(`Delete trading strategy "${strategy.Name}"?`)) {
      return;
    }
    try {
      await apiClient.deleteTradingStrategy(strategy.Id);
      setStrategies((prev) => (prev || []).filter((s) => s.Id !== strategy.Id));
    } catch (err) {
      alert('Failed to delete strategy: ' + (err?.message || err));
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3" style={{ gap: '12px', flexWrap: 'wrap' }}>
        <h1 className="mb-0">Trading Strategies</h1>
        <Button color="success" size="sm" onClick={() => navigate('/trading-strategies/new')}>
          Create Strategy
        </Button>
        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
          <span className="text-muted small">Show</span>
          <ButtonGroup size="sm" aria-label="Trading strategies scope">
            <Button
              color={scope === 'mine' ? 'primary' : 'secondary'}
              outline={scope !== 'mine'}
              onClick={() => setScope('mine')}
              aria-pressed={scope === 'mine'}
            >
              Only mine
            </Button>
            <Button
              color={scope === 'all' ? 'primary' : 'secondary'}
              outline={scope !== 'all'}
              onClick={() => setScope('all')}
              aria-pressed={scope === 'all'}
            >
              All
            </Button>
          </ButtonGroup>
        </div>
      </div>

      {loadError && <Alert color="danger">{loadError}</Alert>}

      {!strategies && !loadError && (
        <div className="text-center py-3"><Spinner /></div>
      )}

      {strategies && strategies.length === 0 && (
        <p className="text-muted mb-0">
          {scope === 'mine'
            ? 'No trading strategies yet. Click "Create Strategy" to add one.'
            : 'No trading strategies exist in the system yet.'}
        </p>
      )}

      {strategies && strategies.length > 0 && (
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Id</th>
              <th>Trading Connector</th>
              <th>Model</th>
              <th>Signal Tag</th>
              <th>Enabled</th>
              <th>Owner</th>
              <th>Created</th>
              <th>Updated</th>
              <th style={{ width: '260px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {strategies.map((s) => {
              // Admins see every strategy in the system; the Owner column
              // lets them tell their own apart from other users' at a
              // glance. Strategies not owned by the current user are
              // strictly read-only — Edit / Delete / Review buttons are
              // hidden, only the View button remains.
              const isOwner = isAuthenticated && user?.Id === s.UserId;
              return (
                <tr key={s.Id}>
                  <td>{s.Id}</td>
                  <td>{s.TradingConnectorName || `#${s.TradingConnectorId}`}</td>
                  <td>{s.ModelName || `#${s.ModelId}`}</td>
                  <td>
                    {s.SignalTag
                      ? <span className="badge bg-info text-dark">{s.SignalTag}</span>
                      : <span className="text-muted">-</span>}
                  </td>
                  <td>
                    {s.Enabled ? (
                      <Badge color="success">Enabled</Badge>
                    ) : (
                      <Badge color="secondary">Disabled</Badge>
                    )}
                    {s.PendingTrade && (
                      <Badge color="warning" className="ml-1">Pending Trade</Badge>
                    )}
                  </td>
                  <td>
                    {isOwner ? (
                      <Badge color="success">Yes</Badge>
                    ) : (
                      <Badge color="secondary">No</Badge>
                    )}
                  </td>
                  <td>{s.getFormattedCreateTime()}</td>
                  <td>{s.getFormattedUpdateTime()}</td>
                  <td>
                    <div className="d-flex" style={{ gap: '8px' }}>
                      <Button
                        color="info"
                        size="sm"
                        tag={Link}
                        to={`/trading-strategies/${s.Id}/view`}
                      >
                        View
                      </Button>
                      {isOwner && s.PendingTrade && (
                        <Button
                          color="warning"
                          size="sm"
                          tag={Link}
                          to={`/trading-strategies/${s.Id}/view/review-pending-trade`}
                        >
                          Review
                        </Button>
                      )}
                      {isOwner && (
                        <Button
                          color="primary"
                          size="sm"
                          tag={Link}
                          to={`/trading-strategies/${s.Id}/edit`}
                        >
                          Edit
                        </Button>
                      )}
                      {isOwner && (
                        <Button color="danger" size="sm" onClick={() => handleDelete(s)}>
                          Delete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};
